import React, { useEffect } from 'react'
import PropTypes, { InferProps } from 'prop-types'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import { useForm } from 'react-hook-form'
import { Box, Button, Flex, Input, Text, Tooltip } from 'pcln-design-system'
import { CONTENT, EMAIL_REGEXP } from './signup-content'
import analytics from '../../utils/analytics'
import { INVALID, VALID } from '../../constants/analytics'

const EmailBannerWrapper = styled(Flex)`
  display: none;

  ${themeGet('mediaQueries.md')} {
    display: inline-block;
    width: auto;
  }

  ${themeGet('mediaQueries.xxl')} {
    display: flex;
  }
`

const EmailFormWrapper = styled(Flex)`
  ${themeGet('mediaQueries.md')} {
    margin-left: 0;
  }

  ${themeGet('mediaQueries.xxl')} {
    margin-left: ${themeGet('space.4')}px;
  }
`

const EmailInput = styled(Input)`
  border-radius: 2px;
  display: inline;
  height: 40px;
  width: 308px;
`

const SignUpLabel = styled(Text)`
  text-align: center;
`

export default function EmailSignUpForm({
  category,
  error,
  submitEmail,
  isLoading
}: InferProps<typeof EmailSignUpForm.propTypes>) {
  const { errors, handleSubmit, register } = useForm({ mode: 'onSubmit' })

  useEffect(() => {
    analytics.fireEvent({
      action: 'Email Signup',
      category,
      label: errors.signUpEmail ? INVALID : VALID
    })
  }, [category, errors])

  return (
    <form onSubmit={handleSubmit(submitEmail)}>
      <EmailBannerWrapper alignItems="center" justifyContent="center" py={2}>
        <SignUpLabel fontWeight="bold" fontSize={15} py={2}>
          {CONTENT.merchandisingMessage}
        </SignUpLabel>
        <EmailFormWrapper
          alignItems="center"
          justifyContent="center"
          ml={4}
          py={2}
        >
          <Box>
            <EmailInput
              id="signUpEmail"
              aria-label="Email Input"
              aria-describedby="email_error"
              name="signUpEmail"
              color={!!error || errors.signUpEmail ? 'red' : ''}
              innerRef={register({
                required: true,
                pattern: EMAIL_REGEXP
              })}
              placeholder={CONTENT.placeHolder}
            />
            <Box id="email_error">
              {errors.signUpEmail && (
                <Tooltip bottom right color="error" zIndex={1}>
                  Please enter a valid email.
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box>
            <Button
              ml={3}
              px={3}
              variation="outline"
              data-autobot-element-id="email-signup-button"
            >
              {isLoading ? CONTENT.buttonSubmittingText : CONTENT.buttonText}
            </Button>
          </Box>
        </EmailFormWrapper>
      </EmailBannerWrapper>
    </form>
  )
}

EmailSignUpForm.propTypes = {
  category: PropTypes.string.isRequired,
  error: PropTypes.any,
  isLoading: PropTypes.bool.isRequired,
  submitEmail: PropTypes.func.isRequired
}
